/**
 * Canonical prefixes (design/os-reorganisation-ecrans § 7, lot R4): Run lives
 * under /run/*, Studio under /studio/*. The former route families (operate,
 * build, builder) only redirect (utils/legacyRoutes.ts).
 */
export const AGENT_SPEC_AUTHORING_PATH = '/studio/agent-spec'
/** @deprecated Same path as AGENT_SPEC_AUTHORING_PATH since the route unification. */
export const BUILDER_AGENT_SPEC_PATH = AGENT_SPEC_AUTHORING_PATH

const ORGANIZATION_ID_PARAM = 'organizationId'
const AGENT_ID_PARAM = 'agentId'
const LEGACY_RUNTIME_TARGET_PARAM = 'deploymentId'
const APPLICATION_KEY_PARAM = 'applicationKey'
const SECTION_PARAM = 'section'
const RESOURCE_ID_PARAM = 'resourceId'

export type AgentSpecHrefOptions = {
  section?: string | null
  resourceId?: string | null
  threadId?: string | null
}

export function isAgentSpecAuthoringPath(pathname: string): boolean {
  return (
    pathname === AGENT_SPEC_AUTHORING_PATH ||
    pathname.startsWith(`${AGENT_SPEC_AUTHORING_PATH}/`)
  )
}

/**
 * When syncing the store back to the URL, keep an explicit deep-link /
 * in-flight `agentId` (or `deploymentId`). A stale persisted selection
 * must not rewrite `?agentId=` before bootstrap adopts the URL.
 */
export function resolveUrlSyncAgentId(
  urlAgentId: string | null,
  storeAgentId: string | null,
): string | null {
  return urlAgentId || storeAgentId
}

/** Surfaces that own `?applicationKey=` as context, not a leftover stamp. */
export function pathnameOwnsApplicationKey(pathname: string): boolean {
  return (
    pathname.startsWith('/studio/applications') ||
    pathname.startsWith('/compose') ||
    pathname.startsWith('/run') ||
    pathname.startsWith('/studio/access') ||
    pathname.startsWith('/studio/messaging') ||
    pathname.startsWith('/studio/web')
  )
}

/**
 * Calisto chat / Agent Spec must not inherit a leftover Application from
 * the store. Stamping `?applicationKey=` onto `/studio/chat` used to make
 * URL-sync replace-navigate the chat URL and cancel an in-flight leaf.
 */
export function resolveUrlSyncApplicationKey(
  pathname: string,
  urlApplicationKey: string | null,
  storeApplicationKey: string | null,
): string | null {
  if (urlApplicationKey) {
    return urlApplicationKey
  }
  if (pathnameOwnsApplicationKey(pathname)) {
    return storeApplicationKey
  }
  return null
}

/** Thread search (`threadId`) is only meaningful on Calisto / Agent Spec. */
export function isStudioThreadSurface(pathname: string): boolean {
  return (
    pathname.startsWith('/studio/chat') ||
    pathname.startsWith('/studio/agent-spec')
  )
}

export function readContextTargetId(
  pathname: string,
  searchParams: URLSearchParams,
): string | null {
  // Agent Spec authoring: agentId only (never fall back to deploymentId).
  if (isAgentSpecAuthoringPath(pathname)) {
    return searchParams.get(AGENT_ID_PARAM)
  }
  // Studio chat / builder surfaces: accept agentId or deploymentId deep-links.
  if (pathname.startsWith('/studio')) {
    return (
      searchParams.get(AGENT_ID_PARAM) ||
      searchParams.get(LEGACY_RUNTIME_TARGET_PARAM)
    )
  }
  return (
    searchParams.get(LEGACY_RUNTIME_TARGET_PARAM) ||
    searchParams.get(AGENT_ID_PARAM)
  )
}

export function readApplicationKey(
  searchParams: URLSearchParams,
): string | null {
  return searchParams.get(APPLICATION_KEY_PARAM)
}

export function reconcileContextSearch({
  pathname,
  search,
  organizationId,
  agentId,
  applicationKey,
}: {
  pathname: string
  search: string
  organizationId: string | null
  agentId: string | null
  applicationKey?: string | null
}): { changed: boolean; search: string } {
  const params = new URLSearchParams(search)
  const initialSearch = params.toString()

  if (organizationId) {
    params.set(ORGANIZATION_ID_PARAM, organizationId)
  } else {
    params.delete(ORGANIZATION_ID_PARAM)
  }

  if (isAgentSpecAuthoringPath(pathname)) {
    params.delete(LEGACY_RUNTIME_TARGET_PARAM)
    if (agentId) {
      params.set(AGENT_ID_PARAM, agentId)
    } else {
      params.delete(AGENT_ID_PARAM)
    }
  } else if (pathname.startsWith('/studio')) {
    if (agentId) {
      params.set(AGENT_ID_PARAM, agentId)
      params.set(LEGACY_RUNTIME_TARGET_PARAM, agentId)
    } else {
      params.delete(AGENT_ID_PARAM)
      params.delete(LEGACY_RUNTIME_TARGET_PARAM)
    }
  } else if (agentId) {
    params.set(LEGACY_RUNTIME_TARGET_PARAM, agentId)
    params.delete(AGENT_ID_PARAM)
  } else {
    params.delete(LEGACY_RUNTIME_TARGET_PARAM)
    params.delete(AGENT_ID_PARAM)
  }

  if (applicationKey) {
    params.set(APPLICATION_KEY_PARAM, applicationKey)
  } else {
    params.delete(APPLICATION_KEY_PARAM)
  }

  const nextSearch = params.toString()
  return { changed: nextSearch !== initialSearch, search: nextSearch }
}

function appendSearch(
  pathname: string,
  params: URLSearchParams,
): string {
  const search = params.toString()
  return search ? `${pathname}?${search}` : pathname
}

export function agentSpecAuthoringHref(
  organizationId: string | null | undefined,
  agentId: string | null | undefined,
  options?: AgentSpecHrefOptions,
): string {
  const params = new URLSearchParams()
  if (organizationId) params.set(ORGANIZATION_ID_PARAM, organizationId)
  if (agentId) params.set(AGENT_ID_PARAM, agentId)
  if (options?.section) params.set(SECTION_PARAM, options.section)
  if (options?.resourceId) params.set(RESOURCE_ID_PARAM, options.resourceId)
  if (options?.threadId) params.set('threadId', options.threadId)
  return appendSearch(AGENT_SPEC_AUTHORING_PATH, params)
}

/** Canonical Studio Agent Spec href used by the shell tree (builder path). */
export function builderAgentSpecHref(
  organizationId: string | null | undefined,
  agentId: string | null | undefined,
  options?: AgentSpecHrefOptions,
): string {
  const params = new URLSearchParams()
  if (organizationId) params.set(ORGANIZATION_ID_PARAM, organizationId)
  if (agentId) params.set(AGENT_ID_PARAM, agentId)
  if (options?.section) params.set(SECTION_PARAM, options.section)
  if (options?.resourceId) params.set(RESOURCE_ID_PARAM, options.resourceId)
  if (options?.threadId) params.set('threadId', options.threadId)
  return appendSearch(BUILDER_AGENT_SPEC_PATH, params)
}

/** Deep-link Agent → Studio chat, preserving org/agent (+ optional thread / app). */
export function builderCalistoHref(
  organizationId: string | null | undefined,
  agentId: string | null | undefined,
  options?: {
    threadId?: string | null
    revisionId?: string | null
    applicationKey?: string | null
    view?: string | null
  },
): string {
  const params = new URLSearchParams()
  if (organizationId) params.set(ORGANIZATION_ID_PARAM, organizationId)
  if (agentId) {
    params.set(AGENT_ID_PARAM, agentId)
    params.set(LEGACY_RUNTIME_TARGET_PARAM, agentId)
  }
  if (options?.applicationKey) params.set(APPLICATION_KEY_PARAM, options.applicationKey)
  if (options?.threadId) params.set('threadId', options.threadId)
  if (options?.revisionId) params.set('revisionId', options.revisionId)
  if (options?.view) params.set('view', options.view)
  return appendSearch('/studio/chat', params)
}

/** Operate leaf href with stable application (and optional agent) query. */
export function operateSurfaceHref(
  pathname: string,
  {
    organizationId,
    applicationKey,
    agentId,
  }: {
    organizationId?: string | null
    applicationKey?: string | null
    agentId?: string | null
  },
): string {
  const params = new URLSearchParams()
  if (organizationId) params.set(ORGANIZATION_ID_PARAM, organizationId)
  if (applicationKey) params.set(APPLICATION_KEY_PARAM, applicationKey)
  if (agentId) params.set(LEGACY_RUNTIME_TARGET_PARAM, agentId)
  return appendSearch(pathname, params)
}

export function operateSessionHref(
  sessionId: string,
  executionId?: string | null,
): string {
  const params = new URLSearchParams({ session: sessionId })
  if (executionId) params.set('executionId', executionId)
  return appendSearch('/run/chat', params)
}

export function applicationReviewHref(
  applicationId: string,
  {
    organizationId,
    applicationKey,
    agentId,
  }: {
    organizationId?: string | null
    applicationKey?: string | null
    agentId?: string | null
  },
): string {
  const params = new URLSearchParams()
  if (organizationId) params.set(ORGANIZATION_ID_PARAM, organizationId)
  if (applicationKey) params.set(APPLICATION_KEY_PARAM, applicationKey)
  if (agentId) {
    params.set(AGENT_ID_PARAM, agentId)
    params.set(LEGACY_RUNTIME_TARGET_PARAM, agentId)
  }
  return appendSearch(`/studio/applications/${applicationId}`, params)
}

export function applicationConfigHref(
  pathname: string,
  {
    organizationId,
    applicationKey,
    agentId,
  }: {
    organizationId?: string | null
    applicationKey?: string | null
    agentId?: string | null
  },
): string {
  const params = new URLSearchParams()
  if (organizationId) params.set(ORGANIZATION_ID_PARAM, organizationId)
  if (applicationKey) params.set(APPLICATION_KEY_PARAM, applicationKey)
  if (agentId) {
    params.set(AGENT_ID_PARAM, agentId)
    params.set(LEGACY_RUNTIME_TARGET_PARAM, agentId)
  }
  return appendSearch(pathname, params)
}
